import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { CustomEventDto } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { STANDARD_EVENTS_ALLOWED_KEY_METRICS } from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { MetricDetailSection } from "@src/features/analytics/metricDetails/MetricDetailSection";
import { MetricDetailSelect } from "@src/features/analytics/metricDetails/MetricDetailSelect";
import { StatSigSection } from "@src/features/analytics/metricDetails/statSig/StatSigSection";
import { TimeSeriesGraphWrapper } from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesGraphWrapper";
import {
  getInitialMetricId,
  metricDescriptions,
} from "@src/features/analytics/metricDetails/utils";
import {
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import {
  getKeyMetricsRich,
  getPrimaryMetricRich,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { UseQueryResult } from "@tanstack/react-query";

const isProfitRelatedStandardMetric = (metricId: string): boolean => {
  // check all standard metrics to see if this metricId corresponds to a profit-related one
  for (const [key, value] of Object.entries(
    STANDARD_EVENTS_ALLOWED_KEY_METRICS
  )) {
    if (
      value.analyticsMetricId === metricId &&
      key.toLowerCase().includes("profit")
    ) {
      return true;
    }
  }
  return false;
};

interface MetricDetailsWrapperProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  dropdownOptions: {
    title: string;
    id: string;
  }[];
  metricFilterIndex?: number;
}

export const MetricDetailsWrapper = ({
  variationOverviewData,
  graphConfigs,
  dropdownOptions,
  metricFilterIndex,
}: MetricDetailsWrapperProps) => {
  const { state } = useLocation();
  const theme = useTheme();
  const experienceKeyMetrics = useSelector(getKeyMetricsRich);
  const primaryMetric = useSelector(getPrimaryMetricRich);
  const shouldShowProfit = useMemo(() => {
    return shouldShowProfitData(variationOverviewData[0]?.data?.metrics || []);
  }, [variationOverviewData]);
  const navigate = useNavigate();

  const [selectedMetricId, setSelectedMetricId] = useImmer<string>(
    getInitialMetricId({
      primaryMetric,
      dropdownOptions,
      graphConfigs,
      shouldShowProfit,
    }) ?? ""
  );

  const filteredDropdownOptions = useMemo(() => {
    if (shouldShowProfit === true) {
      return dropdownOptions;
    }
    return dropdownOptions.filter(
      (option) => !isProfitRelatedStandardMetric(option.id)
    );
  }, [dropdownOptions, shouldShowProfit]);

  const filteredConfigs = useMemo(() => {
    return graphConfigs.filter((config) => {
      return !shouldShowProfitPlaceholder(shouldShowProfit, config);
    });
  }, [graphConfigs, shouldShowProfit]);

  const selectedConfig = useMemo(() => {
    return (
      filteredConfigs.find(
        (c) =>
          c.impactMetric === selectedMetricId ||
          c.metrics[0] === selectedMetricId
      ) || graphConfigs[0]
    );
  }, [selectedMetricId]);

  const statSigConfigs = filteredConfigs.filter(
    (config) => config.hasValueCI || config.hasProbability
  );

  const timeSeriesConfigs = filteredConfigs.filter(
    (config) => config.hasTimeseries
  );

  const isPrimary = useMemo(() => {
    const primaryKeyMetric = experienceKeyMetrics.find(
      (keyMetric) => keyMetric.isPrimary
    );
    if (!primaryKeyMetric) {
      return false;
    }
    if (primaryKeyMetric.standardEventId) {
      return (
        STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryKeyMetric.standardEventId]
          ?.analyticsMetricId === selectedMetricId
      );
    } else {
      return (
        primaryKeyMetric?.experienceCustomMetric?.customEvent?.identifier ===
        selectedMetricId
      );
    }
  }, [selectedMetricId]);

  useEffect(() => {
    const el = document.getElementById(state?.targetId);
    if (el && state?.targetMetricId) {
      window.scrollTo({
        behavior: "smooth",
        top:
          el.getBoundingClientRect().top -
          document.body.getBoundingClientRect().top -
          200,
      });
    }

    if (state?.targetMetricId) {
      setSelectedMetricId(state.targetMetricId);
      const location = window.location;
      void navigate(location.pathname + location.search, {
        replace: true,
        state: {}, // Empty state object to clear previous values
      });
    }
  }, [state?.targetId, state?.targetMetricId]);
  useEffect(() => {
    const foundGraphConfig = graphConfigs.find((config) =>
      config.metrics.includes(selectedMetricId)
    );
    if (
      foundGraphConfig &&
      foundGraphConfig.hasMetricFilterIndex &&
      metricFilterIndex !== undefined
    ) {
      setSelectedMetricId(foundGraphConfig.metrics[metricFilterIndex]);
    }
  }, [metricFilterIndex]);

  if (!selectedMetricId) {
    return null;
  }
  return (
    <Grid id={"metric-details-wrapper"}>
      <Box
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          borderBottomLeftRadius: "10px",
          borderBottomRightRadius: "10px",
          backgroundColor: "white",
          border: `1px solid ${theme.igBorders.core.default}`,
        }}
      >
        <Grid
          container
          sx={{
            padding: "20px 30px",
            borderBottom: `1px solid ${theme.igBorders.core.default}`,
          }}
          direction={"row"}
          alignItems={"center"}
          columnSpacing={8}
        >
          <Grid>
            <IgTypography
              variant={"h2"}
              sx={{ textWrap: "nowrap" }}
              fontWeight={600}
            >
              Metric Details
            </IgTypography>
          </Grid>
          <Grid>
            <MetricDetailSelect
              selectedMetricId={selectedMetricId}
              setSelectedMetricId={setSelectedMetricId}
              dropdownOptions={filteredDropdownOptions}
              isPrimaryMetric={isPrimary}
            />
          </Grid>
          <Grid>
            <IgTypography
              variant={"body2"}
              color={theme.palette.text.secondary}
            >
              {metricDescriptions[selectedMetricId] || ""}
            </IgTypography>
          </Grid>
        </Grid>
        <Grid
          container
          direction={"column"}
          rowSpacing={5}
          sx={{ padding: "40px" }}
        >
          {statSigConfigs.length &&
          (selectedConfig.hasProbability ||
            selectedConfig.hasValueCI ||
            selectedConfig.hasProbability) ? (
            <Grid>
              <MetricDetailSection
                title={"Statistical Significance"}
                metricDetails={{
                  title:
                    filteredDropdownOptions.find(
                      (opt) => opt.id === selectedMetricId
                    )?.title || "",
                  isPrimary,
                }}
              >
                <StatSigSection
                  selectedMetricId={selectedMetricId}
                  graphConfigs={statSigConfigs}
                  variationOverviewData={variationOverviewData}
                />
              </MetricDetailSection>
            </Grid>
          ) : null}

          {timeSeriesConfigs && (
            <Grid>
              <MetricDetailSection
                title={"Time Series"}
                metricDetails={{
                  title:
                    filteredDropdownOptions.find(
                      (opt) => opt.id === selectedMetricId
                    )?.title || "",
                  isPrimary,
                }}
              >
                <TimeSeriesGraphWrapper
                  graphConfigs={timeSeriesConfigs}
                  selectedMetricId={selectedMetricId}
                  overviewDataLoaded={variationOverviewData[0].isSuccess}
                />
              </MetricDetailSection>
            </Grid>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};
